const FAMILY_PAGE_PARAM = 'family_page';
const AUTOPLAY_DELAY = 4500;
const SWIPE_DISTANCE = 40;

export const FAMILY_DEFINITIONS = Object.freeze([
  Object.freeze({
    id: 'little-boss-energy',
    title: 'Little Boss Energy Baby T-Shirt',
    preferredHandle: 'infant-tee-little-boss-energy-pink-baby-shirt',
    members: Object.freeze([
      'little-boss-energy-baby-t-shirt',
      'infant-tee-little-boss-energy-pink-baby-shirt',
      'little-boss-energy-baby-t-shirt-white',
    ]),
    captions: Object.freeze({
      'little-boss-energy-baby-t-shirt': 'Black · White print',
      'infant-tee-little-boss-energy-pink-baby-shirt': 'White · Pink print',
      'little-boss-energy-baby-t-shirt-white': 'White · Dark print',
    }),
  }),
  Object.freeze({
    id: 'ceo-energy',
    title: 'CEO Energy Baby T-Shirt',
    members: Object.freeze([
      'infant-tee-ceo-energy-minimalist-baby-shirt',
      'infant-tee-ceo-energy-pink-text-baby-shirt',
    ]),
    captions: Object.freeze({
      'infant-tee-ceo-energy-minimalist-baby-shirt': 'Black · White print',
      'infant-tee-ceo-energy-pink-text-baby-shirt': 'White · Pink print',
    }),
  }),
]);

function familyForHandle(handle, definitions = FAMILY_DEFINITIONS) {
  return definitions.find((family) => family.members.includes(handle));
}

function uniqueProducts(products) {
  const handles = new Set();
  return products.filter((product) => {
    if (!product?.handle || handles.has(product.handle)) return false;
    handles.add(product.handle);
    return true;
  });
}

export function groupProductFamilies(products, definitions = FAMILY_DEFINITIONS) {
  const unique = uniqueProducts(products);
  const membersByFamily = new Map();

  unique.forEach((product) => {
    const family = familyForHandle(product.handle, definitions);
    if (!family) return;
    const members = membersByFamily.get(family.id) || [];
    members.push(product);
    membersByFamily.set(family.id, members);
  });

  const eligibleFamilies = new Map(
    definitions
      .filter((family) => (membersByFamily.get(family.id)?.length || 0) > 1)
      .map((family) => [family.id, { ...family, products: membersByFamily.get(family.id) }]),
  );
  const renderedFamilies = new Set();

  return unique.flatMap((product) => {
    const family = familyForHandle(product.handle, definitions);
    const groupedFamily = family && eligibleFamilies.get(family.id);
    if (!groupedFamily) return [{ kind: 'product', product }];
    if (renderedFamilies.has(groupedFamily.id)) return [];
    renderedFamilies.add(groupedFamily.id);
    return [{ kind: 'family', family: groupedFamily }];
  });
}

export function paginateFamilyCards(cards, pageSize, requestedPage = 1) {
  const safePageSize = Math.max(1, Number.parseInt(pageSize, 10) || 1);
  const totalPages = Math.max(1, Math.ceil(cards.length / safePageSize));
  const requested = Number.parseInt(requestedPage, 10) || 1;
  const currentPage = Math.min(Math.max(requested, 1), totalPages);
  const start = (currentPage - 1) * safePageSize;

  return { cards: cards.slice(start, start + safePageSize), currentPage, totalPages };
}

export function selectedFamilyProduct(family, handle) {
  return family.products.find((product) => product.handle === handle)
    || family.products.find((product) => product.handle === family.preferredHandle)
    || family.products[0];
}

export function swipeDirection(start, end, minimumDistance = SWIPE_DISTANCE) {
  const horizontalDistance = end.x - start.x;
  const verticalDistance = end.y - start.y;
  if (Math.abs(horizontalDistance) < minimumDistance || Math.abs(horizontalDistance) <= Math.abs(verticalDistance)) return 0;
  return horizontalDistance < 0 ? 1 : -1;
}

export function rotationState({
  reducedMotion = false,
  explicitlyStarted = false,
  persistentlyPaused = false,
  visible = true,
  documentVisible = true,
  hovering = false,
} = {}) {
  const isPaused = persistentlyPaused || (reducedMotion && !explicitlyStarted);
  return {
    isPaused,
    shouldAutoplay: !isPaused && visible && documentVisible && !hovering,
  };
}

function productFromArticle(article) {
  const image = article.querySelector('img');
  return {
    article,
    handle: article.dataset.productHandle,
    title: article.dataset.productTitle || '',
    type: article.dataset.productType || '',
    url: article.dataset.productUrl || '#',
    price: article.dataset.productPrice || '',
    priceVaries: article.dataset.productPriceVaries === 'true',
    available: article.dataset.productAvailable !== 'false',
    image,
  };
}

function productCaption(family, product) {
  return family.captions?.[product.handle] || product.title;
}

function buildPageUrl(page, pageParam, sectionId) {
  const url = new URL(window.location.href);
  url.searchParams.delete(FAMILY_PAGE_PARAM);
  url.searchParams.set(pageParam, String(page));
  url.searchParams.set('section_id', sectionId);
  return url;
}

function familyPageUrl(page, pageParam) {
  const url = new URL(window.location.href);
  url.searchParams.delete(pageParam);
  url.searchParams.delete('section_id');
  url.searchParams.set(FAMILY_PAGE_PARAM, String(page));
  return `${url.pathname}${url.search}${url.hash}`;
}

function iconImage(url) {
  const image = document.createElement('img');
  image.src = url;
  image.alt = '';
  image.width = 18;
  image.height = 18;
  return image;
}

class FamilyCarousel {
  constructor(article, family, options) {
    this.article = article;
    this.family = family;
    this.products = family.products;
    this.pauseIcon = options.pauseIcon;
    this.playIcon = options.playIcon;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.index = this.products.indexOf(selectedFamilyProduct(family));
    this.explicitlyStarted = false;
    this.persistentlyPaused = false;
    this.visible = true;
    this.hovering = false;
    this.documentVisible = !document.hidden;
    this.timer = null;
    this.touchStart = null;
    this.suppressSlideClick = false;
    this.suppressSlideClickTimer = null;
    this.render();
    this.observeVisibility();
    this.syncAutoplay();
  }

  render() {
    const media = document.createElement('div');
    media.className = 'ms-family-card__media';
    media.setAttribute('aria-roledescription', 'carousel');
    media.setAttribute('aria-label', `${this.family.title} product images`);
    this.slides = this.products.map((product, index) => {
      const link = document.createElement('a');
      link.className = 'ms-family-card__slide';
      link.href = product.url;
      link.dataset.msFamilySlide = product.handle;
      const image = product.image?.cloneNode(true) || document.createElement('img');
      image.className = 'ms-family-card__image';
      image.alt = product.title;
      image.removeAttribute('id');
      link.append(image);
      media.append(link);
      this.bindSlide(link, index);
      return link;
    });

    this.pauseButton = document.createElement('button');
    this.pauseButton.className = 'ms-family-card__pause';
    this.pauseButton.type = 'button';
    this.pauseButton.addEventListener('pointerdown', (event) => event.preventDefault());
    this.pauseButton.addEventListener('click', () => this.togglePause());
    media.append(this.pauseButton);

    this.thumbnailList = document.createElement('div');
    this.thumbnailList.className = 'ms-family-card__thumbnails';
    this.thumbnailList.setAttribute('aria-label', 'Choose product image');
    this.thumbnails = this.products.map((product, index) => {
      const button = document.createElement('button');
      button.className = 'ms-family-card__thumbnail';
      button.type = 'button';
      button.dataset.msFamilyThumbnail = product.handle;
      button.setAttribute('aria-label', `Show ${productCaption(this.family, product)}`);
      const image = product.image?.cloneNode(true) || document.createElement('img');
      image.className = 'ms-family-card__thumbnail-image';
      image.alt = '';
      image.removeAttribute('id');
      button.append(image);
      button.addEventListener('click', () => this.select(index, { manual: true }));
      this.thumbnailList.append(button);
      return button;
    });

    const copy = document.createElement('div');
    copy.className = 'ms-family-card__copy';
    this.caption = document.createElement('p');
    this.caption.className = 'ms-family-card__caption';
    this.category = document.createElement('p');
    this.category.className = 'ms-family-card__category';
    this.category.textContent = (this.products[0].type || 'Kids clothes').toUpperCase();
    this.title = document.createElement('h2');
    this.titleLink = document.createElement('a');
    this.titleLink.textContent = this.family.title;
    this.title.append(this.titleLink);
    this.price = document.createElement('p');
    this.price.className = 'ms-family-card__price';
    this.action = document.createElement('div');
    this.action.className = 'ms-family-card__action';
    copy.append(this.caption, this.category, this.title, this.price, this.action);
    this.article.replaceChildren(media, this.thumbnailList, copy);

    this.article.addEventListener('mouseenter', () => { this.hovering = true; this.syncAutoplay(); });
    this.article.addEventListener('mouseleave', () => { this.hovering = false; this.syncAutoplay(); });
    this.article.addEventListener('focusin', () => this.pauseRotation());
    this.article.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') this.touchStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
    });
    this.article.addEventListener('pointerup', (event) => {
      if (!this.touchStart || event.pointerId !== this.touchStart.id) return;
      const direction = swipeDirection(this.touchStart, { x: event.clientX, y: event.clientY });
      this.touchStart = null;
      if (!direction) return;
      this.suppressSlideClick = true;
      window.clearTimeout(this.suppressSlideClickTimer);
      this.suppressSlideClickTimer = window.setTimeout(() => { this.suppressSlideClick = false; }, 750);
      this.select(this.index + direction, { manual: true });
    });
    this.article.addEventListener('pointercancel', () => { this.touchStart = null; });
    this.update();
  }

  bindSlide(slide, index) {
    slide.addEventListener('click', (event) => {
      if (this.suppressSlideClick) {
        event.preventDefault();
        this.suppressSlideClick = false;
        return;
      }
      if (index !== this.index) {
        event.preventDefault();
        this.select(index, { manual: true });
      }
    });
  }

  observeVisibility() {
    if (!('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.syncAutoplay();
    }, { threshold: 0.15 });
    this.observer.observe(this.article);
  }

  setDocumentVisibility(visible) {
    this.documentVisible = visible;
    this.syncAutoplay();
  }

  select(index, { manual = false } = {}) {
    this.index = (index + this.products.length) % this.products.length;
    if (manual) this.pauseRotation({ update: false });
    this.update();
    this.syncAutoplay();
  }

  togglePause() {
    if (rotationState(this.rotationOptions()).isPaused) this.startRotation();
    else this.pauseRotation({ update: false });
    this.update();
    this.syncAutoplay();
  }

  pauseRotation({ update = true } = {}) {
    this.persistentlyPaused = true;
    this.explicitlyStarted = false;
    if (update) {
      this.update();
      this.syncAutoplay();
    }
  }

  startRotation() {
    this.persistentlyPaused = false;
    this.explicitlyStarted = true;
    this.update();
    this.syncAutoplay();
  }

  rotationOptions() {
    return {
      reducedMotion: this.reducedMotion,
      explicitlyStarted: this.explicitlyStarted,
      persistentlyPaused: this.persistentlyPaused,
      visible: this.visible,
      documentVisible: this.documentVisible,
      hovering: this.hovering,
    };
  }

  update() {
    const product = this.products[this.index];
    this.slides.forEach((slide, index) => {
      const active = index === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      slide.tabIndex = active ? 0 : -1;
    });
    this.thumbnails.forEach((thumbnail, index) => {
      const active = index === this.index;
      thumbnail.classList.toggle('is-active', active);
      thumbnail.setAttribute('aria-pressed', String(active));
    });
    this.caption.textContent = productCaption(this.family, product);
    this.titleLink.href = product.url;
    this.price.textContent = `${product.priceVaries ? 'From ' : ''}${product.price}`;
    this.action.replaceChildren();
    if (product.available) {
      const link = document.createElement('a');
      link.href = product.url;
      link.textContent = 'Choose size';
      this.action.append(link);
    } else {
      const soldOut = document.createElement('span');
      soldOut.textContent = 'Sold out';
      soldOut.setAttribute('aria-disabled', 'true');
      this.action.append(soldOut);
    }
    const paused = rotationState(this.rotationOptions()).isPaused;
    this.pauseButton.replaceChildren(iconImage(paused ? this.playIcon : this.pauseIcon));
    this.pauseButton.setAttribute('aria-label', paused ? 'Start product image rotation' : 'Pause product image rotation');
    this.pauseButton.setAttribute('aria-pressed', String(paused));
  }

  syncAutoplay() {
    window.clearTimeout(this.timer);
    this.timer = null;
    if (!rotationState(this.rotationOptions()).shouldAutoplay) return;
    this.timer = window.setTimeout(() => {
      this.select(this.index + 1);
    }, AUTOPLAY_DELAY);
  }

  destroy() {
    window.clearTimeout(this.timer);
    window.clearTimeout(this.suppressSlideClickTimer);
    this.observer?.disconnect();
  }
}

class ProductFamilyGrid {
  constructor(grid) {
    this.grid = grid;
    this.section = grid.closest('section');
    this.sectionId = grid.dataset.msFamilySection;
    this.pageSize = Number.parseInt(grid.dataset.msFamilyPageSize, 10) || 12;
    this.nativeCurrentPage = Number.parseInt(grid.dataset.msFamilyCurrentPage, 10) || 1;
    this.nativeTotalPages = Number.parseInt(grid.dataset.msFamilyTotalPages, 10) || 1;
    this.nativeTotalProducts = Number.parseInt(grid.dataset.msFamilyTotalProducts, 10) || 0;
    this.pageParam = grid.dataset.msFamilyPageParam || 'page';
    this.pauseIcon = grid.dataset.msFamilyPauseIcon;
    this.playIcon = grid.dataset.msFamilyPlayIcon;
    this.nativePagination = this.section?.querySelector('[data-ms-family-pagination]');
    this.carousels = [];
    this.destroyed = false;
  }

  async load() {
    const pages = new Map([[this.nativeCurrentPage, this.extractProducts(this.grid)]]);
    try {
      await Promise.all(Array.from({ length: this.nativeTotalPages }, (_, index) => index + 1)
        .filter((page) => page !== this.nativeCurrentPage)
        .map(async (page) => pages.set(page, await this.fetchProducts(page))));
      const products = Array.from(pages.entries())
        .sort(([left], [right]) => left - right)
        .flatMap(([, pageProducts]) => pageProducts);
      const uniqueProductCount = new Set(products.map((product) => product.handle).filter(Boolean)).size;
      if (uniqueProductCount !== this.nativeTotalProducts) {
        throw new Error(`expected ${this.nativeTotalProducts} unique products, found ${uniqueProductCount}`);
      }
      if (this.destroyed) return;
      this.cards = groupProductFamilies(products);
      if (!this.cards.some((card) => card.kind === 'family')) return;
      this.setupPagination();
      this.showPage(this.familyPageFromUrl(), { replace: true });
    } catch (error) {
      console.warn('Mountainstead product family cards could not load; keeping native collection cards.', error);
    }
  }

  extractProducts(grid) {
    return Array.from(grid.querySelectorAll(':scope > [data-ms-family-product]')).map(productFromArticle);
  }

  async fetchProducts(page) {
    const response = await fetch(buildPageUrl(page, this.pageParam, this.sectionId).toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) throw new Error(`page ${page} returned ${response.status}`);
    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    const matchingGrid = Array.from(documentFragment.querySelectorAll('[data-ms-family-grid]'))
      .find((grid) => grid.dataset.msFamilySection === this.sectionId);
    if (!matchingGrid) throw new Error(`page ${page} did not contain section ${this.sectionId}`);
    return this.extractProducts(matchingGrid);
  }

  setupPagination() {
    if (!this.nativePagination) {
      this.nativePagination = document.createElement('nav');
      this.nativePagination.className = 'ms-family-pagination';
      this.nativePagination.setAttribute('aria-label', 'Product pages');
      this.grid.insertAdjacentElement('afterend', this.nativePagination);
    }
    this.nativePagination.classList.add('ms-family-pagination');
    this.nativePagination.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-ms-family-page]');
      if (!link || !this.nativePagination.contains(link)) return;
      event.preventDefault();
      this.showPage(Number.parseInt(link.dataset.msFamilyPage, 10), { push: true });
    });
  }

  familyPageFromUrl() {
    return Number.parseInt(new URL(window.location.href).searchParams.get(FAMILY_PAGE_PARAM), 10)
      || this.nativeCurrentPage;
  }

  showPage(page, { push = false, replace = false } = {}) {
    if (this.destroyed || !this.cards) return;
    const result = paginateFamilyCards(this.cards, this.pageSize, page);
    this.carousels.forEach((carousel) => carousel.destroy());
    this.carousels = [];
    const fragment = document.createDocumentFragment();
    result.cards.forEach((card) => {
      if (card.kind === 'product') fragment.append(card.product.article.cloneNode(true));
      else fragment.append(this.createFamilyCard(card.family));
    });
    this.grid.replaceChildren(fragment);
    this.currentPage = result.currentPage;
    this.totalPages = result.totalPages;
    this.renderPagination();
    if (push || replace || page !== result.currentPage) {
      const url = familyPageUrl(result.currentPage, this.pageParam);
      window.history[push ? 'pushState' : 'replaceState']({}, '', url);
    }
  }

  createFamilyCard(family) {
    const article = document.createElement('article');
    article.className = `${family.products[0].article.className} ms-product-family-card`;
    article.dataset.msFamilyCard = family.id;
    const carousel = new FamilyCarousel(article, family, {
      pauseIcon: this.pauseIcon,
      playIcon: this.playIcon,
    });
    this.carousels.push(carousel);
    return article;
  }

  renderPagination() {
    this.nativePagination.replaceChildren();
    this.nativePagination.hidden = this.totalPages <= 1;
    if (this.totalPages <= 1) return;
    const pages = [];
    if (this.currentPage > 1) pages.push(['Previous', this.currentPage - 1]);
    for (let page = 1; page <= this.totalPages; page += 1) pages.push([String(page), page]);
    if (this.currentPage < this.totalPages) pages.push(['Next', this.currentPage + 1]);
    pages.forEach(([label, page]) => {
      const link = document.createElement('a');
      link.href = familyPageUrl(page, this.pageParam);
      link.dataset.msFamilyPage = page;
      link.textContent = label;
      if (page === this.currentPage) link.setAttribute('aria-current', 'page');
      this.nativePagination.append(link);
    });
  }

  setDocumentVisibility(visible) {
    this.carousels.forEach((carousel) => carousel.setDocumentVisibility(visible));
  }

  destroy() {
    this.destroyed = true;
    this.carousels.forEach((carousel) => carousel.destroy());
    this.carousels = [];
  }
}

const activeGrids = new Set();

function initialize(root = document) {
  root.querySelectorAll?.('[data-ms-family-grid][data-ms-family-enabled="true"]').forEach((grid) => {
    if (Array.from(activeGrids).some((controller) => controller.grid === grid)) return;
    const controller = new ProductFamilyGrid(grid);
    activeGrids.add(controller);
    controller.load();
  });
}

function destroyWithin(root) {
  activeGrids.forEach((controller) => {
    if (root.contains?.(controller.section) || root === controller.section) {
      controller.destroy();
      activeGrids.delete(controller);
    }
  });
}

function setupBrowserBehavior() {
  initialize();
  document.addEventListener('visibilitychange', () => {
    activeGrids.forEach((controller) => controller.setDocumentVisibility(!document.hidden));
  });
  window.addEventListener('popstate', () => {
    activeGrids.forEach((controller) => controller.showPage(controller.familyPageFromUrl(), { replace: true }));
  });
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
  document.addEventListener('shopify:section:load', (event) => {
    destroyWithin(event.target);
    initialize(event.target);
  });
  const removalObserver = new MutationObserver(() => {
    activeGrids.forEach((controller) => {
      if (!controller.grid.isConnected) {
        controller.destroy();
        activeGrids.delete(controller);
      }
    });
  });
  removalObserver.observe(document.body, { childList: true, subtree: true });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setupBrowserBehavior, { once: true });
  else setupBrowserBehavior();
}
